import { useQuery } from "@tanstack/react-query";
import { DimensionsInstance } from "@/types/integration";
import { useIframePidgetSettings } from "@workspace/utils-react";
import { useAuth } from "@/providers/AuthProvider";

/**
 * Hook to fetch available Dimensions database instances from the backend.
 * Returns the list of instances the user's organisation has access to.
 */
export function useDimensionsInstances(enabled: boolean = true) {
  const settings = useIframePidgetSettings<{ apiUrl?: string }>();
  const apiUrl = settings.apiUrl;
  const { getAccessToken, instanceId } = useAuth();

  return useQuery({
    queryKey: ["dimensions-instances", instanceId, apiUrl],
    queryFn: async (): Promise<DimensionsInstance[]> => {
      if (!apiUrl) {
        throw new Error("API URL is not configured. Please check pidget manifest settings.");
      }

      const headers: Record<string, string> = {};

      if (getAccessToken) {
        try {
          const token = await getAccessToken();
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }
        } catch (error) {
          console.error("Failed to get access token:", error);
          throw new Error("Authentication failed. Please try logging in again.");
        }
      }

      if (instanceId) {
        headers["X-Instance-Id"] = instanceId;
      }

      const response = await fetch(`${apiUrl}/api/dimensions/instances`, {
        headers,
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => "Unknown error");
        throw new Error(`Failed to fetch dimensions instances: ${response.status} - ${errorBody}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Invalid response: expected array of instances");
      }

      return data as DimensionsInstance[];
    },
    enabled: enabled && !!instanceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}
